/**
 * API client for vendor ingestion (vendor credentials + ingestion runs).
 * Mirrors backend/routers/ingestion.py response shapes.
 *
 * GET endpoints require `view_vendor_credentials` (admin + system_admin).
 * Write/run endpoints require `manage_vendor_credentials` /
 * `trigger_vendor_ingestion` (system_admin only).
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY)
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Response shapes — mirror backend/schemas.py
// ---------------------------------------------------------------------------

export interface VendorCredential {
  id: number
  dealer_group_id: number
  vendor_name: string
  transport_type: string
  cron_expression: string | null
  is_enabled: boolean
  last_run_at: string | null
  last_success_at: string | null
  last_error_message: string | null
  created_at: string
  updated_at: string
}

export interface IngestionRun {
  id: number
  vendor_credential_id: number
  dealer_group_id: number
  vendor_name: string
  transport_type: string
  trigger_type: string
  triggered_by_user_id: number | null
  status: string
  started_at: string
  completed_at: string | null
  bytes_fetched: number | null
  rows_ingested: number | null
  landed_file_path: string | null
  error_message: string | null
  error_traceback: string | null
}

// ---------------------------------------------------------------------------
// Read fetchers — only what the read-only Integrations page needs.
// Write/test/trigger fetchers will be added in the follow-up commit that
// builds the configuration form.
// ---------------------------------------------------------------------------

export async function listVendorCredentials(
  dealerGroupId?: number | null,
): Promise<VendorCredential[]> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await fetch(`${API.baseUrl}${API.endpoints.vendorCredentials}${qs}`, {
    headers: authHeaders(),
  })
  return handleResponse<VendorCredential[]>(res)
}

export interface ListIngestionRunsParams {
  vendorName?: string
  status?: string
  since?: string
  limit?: number
  dealerGroupId?: number | null
}

export async function listIngestionRuns(
  params: ListIngestionRunsParams = {},
): Promise<IngestionRun[]> {
  const search = new URLSearchParams()
  if (params.vendorName) search.set("vendor_name", params.vendorName)
  if (params.status) search.set("status", params.status)
  if (params.since) search.set("since", params.since)
  if (params.dealerGroupId != null) search.set("dealer_group_id", String(params.dealerGroupId))
  if (params.limit !== undefined) search.set("limit", String(params.limit))

  const qs = search.toString()
  const url = `${API.baseUrl}${API.endpoints.ingestionRuns}${qs ? `?${qs}` : ""}`
  const res = await fetch(url, { headers: authHeaders() })
  return handleResponse<IngestionRun[]>(res)
}

// ---------------------------------------------------------------------------
// Write fetchers — gated server-side by manage_vendor_credentials /
// trigger_vendor_ingestion (system_admin only).
// ---------------------------------------------------------------------------

export interface VendorCredentialCreate {
  vendor_name: string
  transport_type: string
  config: Record<string, unknown>
  cron_expression?: string | null
  is_enabled?: boolean
  dealer_group_id?: number | null
}

export interface VendorCredentialUpdate {
  vendor_name?: string
  transport_type?: string
  config?: Record<string, unknown>
  cron_expression?: string | null
  is_enabled?: boolean
}

export interface VendorCredentialTestResult {
  success: boolean
  bytes_fetched: number | null
  suggested_filename: string | null
  error_message: string | null
}

export async function createVendorCredential(
  payload: VendorCredentialCreate,
): Promise<VendorCredential> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.vendorCredentials}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  })
  return handleResponse<VendorCredential>(res)
}

export async function updateVendorCredential(
  id: number,
  payload: VendorCredentialUpdate,
): Promise<VendorCredential> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.vendorCredentials}/${id}`,
    {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    },
  )
  return handleResponse<VendorCredential>(res)
}

export async function deleteVendorCredential(id: number): Promise<void> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.vendorCredentials}/${id}`,
    { method: "DELETE", headers: authHeaders() },
  )
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through
    }
    throw new Error(detail)
  }
}

export async function testVendorCredential(
  id: number,
): Promise<VendorCredentialTestResult> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.vendorCredentials}/${id}/test`,
    { method: "POST", headers: authHeaders() },
  )
  return handleResponse<VendorCredentialTestResult>(res)
}

export async function triggerIngestionRun(
  vendorCredentialId: number,
): Promise<IngestionRun> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.ingestionRuns}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ vendor_credential_id: vendorCredentialId }),
  })
  return handleResponse<IngestionRun>(res)
}

// ---------------------------------------------------------------------------
// Website-scrape config (Dealer.com) — per-group site the Website Audit and
// inventory ledger scrape. Mirrors WebsiteScrapeConfigResponse/-Update.
// ---------------------------------------------------------------------------

export interface WebsiteScrapeConfig {
  dealer_group_id: number
  website_base_url: string | null
  dealercom_site_id: string | null
}

export async function fetchWebsiteScrapeConfig(
  dealerGroupId: number,
): Promise<WebsiteScrapeConfig> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteScrapeConfig}?dealer_group_id=${dealerGroupId}`,
    { headers: authHeaders() },
  )
  return handleResponse<WebsiteScrapeConfig>(res)
}

export async function updateWebsiteScrapeConfig(
  dealerGroupId: number,
  body: { website_base_url: string | null; dealercom_site_id: string | null },
): Promise<WebsiteScrapeConfig> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.websiteScrapeConfig}?dealer_group_id=${dealerGroupId}`,
    { method: "PUT", headers: authHeaders(), body: JSON.stringify(body) },
  )
  return handleResponse<WebsiteScrapeConfig>(res)
}
